import type { API } from "@editorjs/editorjs";
import { make } from "@/editor/utils/dom";
import { FaqConfig, FaqData } from "./types";

interface Nodes {
  wrapper: HTMLElement;
  faqContainer: HTMLElement;
  titleContainer: HTMLElement;
  faqList: {
    id: string;
    container: HTMLElement;
    addUpButton?: HTMLButtonElement;
    addDownButton?: HTMLButtonElement;
    moveUpButton?: HTMLButtonElement;
    moveDownButton?: HTMLButtonElement;
    deleteButton?: HTMLButtonElement;
    questionContainer: HTMLElement;
    answerContainer: HTMLElement;
    controlsContainer?: HTMLElement;
  }[];
}

interface ConstructorParams {
  data?: FaqData;
  api: API;
  config: FaqConfig;
  readOnly: boolean;
}

export default class Ui {
  public nodes: Nodes;

  private api: API;

  private config: FaqConfig;

  private readOnly: boolean;

  constructor({ data, api, config, readOnly }: ConstructorParams) {
    this.api = api;
    this.config = config;
    this.readOnly = readOnly;
    this.nodes = {
      wrapper: make("div", [this.CSS.baseClass, this.CSS.wrapper]),
      faqContainer: make("div", [this.CSS.faqContainer]),
      titleContainer: make("h2", [this.CSS.titleContainer], {
        contentEditable: (!this.readOnly).toString(),
      }),
      faqList: data?.faqs.map((faq) => this.generateFaqItem(faq.id)) || [
        this.generateFaqItem(),
      ],
    };
    this.nodes.faqList.forEach((faqItem) => {
      faqItem.questionContainer.innerHTML =
        data?.faqs.find((f) => f.id === faqItem.id)?.question || "";
      faqItem.answerContainer.innerHTML =
        data?.faqs.find((f) => f.id === faqItem.id)?.answer || "";
      this.appendFaqItemElements(faqItem);
    });
    this.nodes.titleContainer.innerHTML =
      data?.title || this.config?.title || "Frequently Asked Questions";
    this.nodes.wrapper.appendChild(this.nodes.titleContainer);
    this.nodes.wrapper.appendChild(this.nodes.faqContainer);
  }

  public render(): HTMLElement {
    this.updateControlStates();
    return this.nodes.wrapper;
  }

  private generateFaqItem(id?: string): Nodes["faqList"][0] {
    const container = make("div", [this.CSS.faqListContainer]);
    const questionContainer = make("div", [this.CSS.questionContainer], {
      "data-placeholder": "Enter your question here",
      contentEditable: (!this.readOnly).toString(),
    });
    const answerContainer = make("div", [this.CSS.answerContainer], {
      "data-placeholder":
        "Enter the answer here (supports bold, italic, links, etc.)",
      contentEditable: (!this.readOnly).toString(),
    });

    const faqItem = {
      id: id || crypto.randomUUID(),
      container,
      questionContainer,
      answerContainer,
    };

    if (!this.readOnly) {
      this.attachControls(faqItem);
    }

    return faqItem;
  }

  private attachControls(faqItem: Nodes["faqList"][0]) {
    const controlsContainer = make("div", [this.CSS.controlsContainer]);

    // Add Up Button
    const addUpButton = make(
      "button",
      [this.CSS.button, this.CSS.addUpButton],
      {
        type: "button",
        title: "Add FAQ above",
      }
    );
    addUpButton.innerHTML = this.getAddUpIcon();
    addUpButton.addEventListener("click", () => this.handleAddUp(faqItem.id));

    // Add Down Button
    const addDownButton = make(
      "button",
      [this.CSS.button, this.CSS.addDownButton],
      {
        type: "button",
        title: "Add FAQ below",
      }
    );
    addDownButton.innerHTML = this.getAddDownIcon();
    addDownButton.addEventListener("click", () =>
      this.handleAddDown(faqItem.id)
    );

    // Move Up Button
    const moveUpButton = make(
      "button",
      [this.CSS.button, this.CSS.moveUpButton],
      {
        type: "button",
        title: "Move up",
      }
    );
    moveUpButton.innerHTML = this.getMoveUpIcon();
    moveUpButton.addEventListener("click", () => this.handleMoveUp(faqItem.id));

    // Move Down Button
    const moveDownButton = make(
      "button",
      [this.CSS.button, this.CSS.moveDownButton],
      {
        type: "button",
        title: "Move down",
      }
    );
    moveDownButton.innerHTML = this.getMoveDownIcon();
    moveDownButton.addEventListener("click", () =>
      this.handleMoveDown(faqItem.id)
    );

    // Delete Button
    const deleteButton = make(
      "button",
      [this.CSS.button, this.CSS.deleteButton],
      {
        type: "button",
        title: "Delete FAQ",
      }
    );
    deleteButton.innerHTML = this.getDeleteIcon();
    deleteButton.addEventListener("click", () => this.handleDelete(faqItem.id));

    controlsContainer.appendChild(addUpButton);
    controlsContainer.appendChild(addDownButton);
    controlsContainer.appendChild(moveUpButton);
    controlsContainer.appendChild(moveDownButton);
    controlsContainer.appendChild(deleteButton);

    faqItem.addUpButton = addUpButton as HTMLButtonElement;
    faqItem.addDownButton = addDownButton as HTMLButtonElement;
    faqItem.moveUpButton = moveUpButton as HTMLButtonElement;
    faqItem.moveDownButton = moveDownButton as HTMLButtonElement;
    faqItem.deleteButton = deleteButton as HTMLButtonElement;
    faqItem.controlsContainer = controlsContainer;
  }

  private appendFaqItemElements(faqItem: Nodes["faqList"][0]) {
    faqItem.container.appendChild(faqItem.questionContainer);
    faqItem.container.appendChild(faqItem.answerContainer);
    if (faqItem.controlsContainer) {
      faqItem.container.appendChild(faqItem.controlsContainer);
    }
    this.nodes.faqContainer.appendChild(faqItem.container);
  }

  private handleAddUp(id: string) {
    const index = this.nodes.faqList.findIndex((item) => item.id === id);
    if (index === -1) return;

    const newFaqItem = this.generateFaqItem();
    this.nodes.faqList.splice(index, 0, newFaqItem);

    this.appendFaqItemElements(newFaqItem);

    this.nodes.faqContainer.insertBefore(
      newFaqItem.container,
      this.nodes.faqList[index + 1].container
    );
    this.updateControlStates();
  }

  private handleAddDown(id: string) {
    const index = this.nodes.faqList.findIndex((item) => item.id === id);
    if (index === -1) return;

    const newFaqItem = this.generateFaqItem();
    this.nodes.faqList.splice(index + 1, 0, newFaqItem);
    this.appendFaqItemElements(newFaqItem);

    const referenceNode = this.nodes.faqContainer.children[index] || null;
    referenceNode.after(newFaqItem.container);
    this.updateControlStates();
  }

  private handleMoveUp(id: string) {
    const index = this.nodes.faqList.findIndex((item) => item.id === id);
    if (index <= 0) return;

    // Swap with previous item
    const temp = this.nodes.faqList[index];
    this.nodes.faqList[index] = this.nodes.faqList[index - 1];
    this.nodes.faqList[index - 1] = temp;

    // Update DOM
    this.nodes.faqContainer.insertBefore(
      temp.container,
      this.nodes.faqList[index].container
    );

    this.updateControlStates();
  }

  private handleMoveDown(id: string) {
    const index = this.nodes.faqList.findIndex((item) => item.id === id);
    if (index === -1 || index >= this.nodes.faqList.length - 1) return;

    // Swap with next item
    const temp = this.nodes.faqList[index];
    this.nodes.faqList[index] = this.nodes.faqList[index + 1];
    this.nodes.faqList[index + 1] = temp;

    // Update DOM
    if (index + 2 < this.nodes.faqList.length) {
      this.nodes.faqContainer.insertBefore(
        temp.container,
        this.nodes.faqList[index + 2].container
      );
    } else {
      this.nodes.faqContainer.appendChild(temp.container);
    }

    this.updateControlStates();
  }

  private handleDelete(id: string) {
    const index = this.nodes.faqList.findIndex((item) => item.id === id);
    if (index === -1) return;

    // Prevent deleting the last item
    if (this.nodes.faqList.length === 1) {
      return;
    }

    const faqItem = this.nodes.faqList[index];
    faqItem.container.remove();
    this.nodes.faqList.splice(index, 1);

    this.updateControlStates();
  }

  private updateControlStates() {
    this.nodes.faqList.forEach((item, index) => {
      if (item.moveUpButton) {
        item.moveUpButton.disabled = index === 0;
      }
      if (item.moveDownButton) {
        item.moveDownButton.disabled = index === this.nodes.faqList.length - 1;
      }
      if (item.deleteButton) {
        item.deleteButton.disabled = this.nodes.faqList.length === 1;
      }
    });
  }

  public addFaqItem() {
    const faqItem = this.generateFaqItem();
    this.nodes.faqList.push(faqItem);
    this.appendFaqItemElements(faqItem);
    this.updateControlStates();
  }

  public get getUiData() {
    const faqs = this.nodes.faqList.map((faqItem) => ({
      id: faqItem.id,
      question: faqItem.questionContainer.innerHTML,
      answer: faqItem.answerContainer.innerHTML,
    }));
    return {
      title: this.nodes.titleContainer.innerHTML,
      faqs,
    };
  }

  private get CSS(): Record<string, string> {
    return {
      baseClass: this.api.styles.block,
      wrapper: "faq-tool",
      faqContainer: "faq-tool__faq-container",
      faqListContainer: "faq-tool__faq-list-container",
      titleContainer: "faq-tool__title-container",
      questionContainer: "faq-tool__question-container",
      answerContainer: "faq-tool__answer-container",
      controlsContainer: "faq-tool__controls-container",
      button: "faq-tool__button",
      addUpButton: "faq-tool__add-up-button",
      addDownButton: "faq-tool__add-down-button",
      moveUpButton: "faq-tool__move-up-button",
      moveDownButton: "faq-tool__move-down-button",
      deleteButton: "faq-tool__delete-button",
    };
  }

  private getAddUpIcon(): string {
    return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M7.3 1V7.3H1V8.7H7.3V15H8.7V8.7H15V7.3H8.7V1H7.3Z" fill="#6B7280"/>
    <rect x="11" width="5" height="5" fill="url(#pattern0_4_2)"/>
    <defs>
    <pattern id="pattern0_4_2" patternContentUnits="objectBoundingBox" width="1" height="1">
    <use xlink:href="#image0_4_2" transform="scale(0.0333333)"/>
    </pattern>
    <image id="image0_4_2" width="30" height="30" preserveAspectRatio="none" xlink:href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAeCAYAAAA7MK6iAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAz0lEQVR4nGNgGKKAka62ZRTXi2UXNZzPKmy4mZdXL0cXS3NzW0WziuovZRc1/AfjwvoHOcXNivS1tIgOlufispSWlucSspQWlucSayk1Lc8l1VIqWc4IyjIkWwrFoKyWljaTdUhZDAKMmRXtgsg4u7DhOzaL0gpaJJHV1dfXMzFQE2QV1X/DZnFhYS8nVS1CB6MWZ49aTCuQNWpx0ajFNAJZoxYX0cviwvo7GHVvUf0bmjfuc4obPbOK6l8gW5pT1BjKQC+QCa3w6d6NoRQAAMvu91kVpuyFAAAAAElFTkSuQmCC"/>
    </defs>
    </svg>
`;
  }

  private getAddDownIcon(): string {
    return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M7.3 1V7.3H1V8.7H7.3V15H8.7V8.7H15V7.3H8.7V1H7.3Z" fill="#6B7280"/>
    <rect x="16" y="16" width="5" height="5" transform="rotate(180 16 16)" fill="url(#pattern0_4_5)"/>
    <defs>
    <pattern id="pattern0_4_5" patternContentUnits="objectBoundingBox" width="1" height="1">
    <use xlink:href="#image0_4_5" transform="scale(0.0333333)"/>
    </pattern>
    <image id="image0_4_5" width="30" height="30" preserveAspectRatio="none" xlink:href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAeCAYAAAA7MK6iAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAz0lEQVR4nGNgGKKAka62ZRTXi2UXNZzPKmy4mZdXL0cXS3NzW0WziuovZRc1/AfjwvoHOcXNivS1tIgOlufispSWlucSspQWlucSayk1Lc8l1VIqWc4IyjIkWwrFoKyWljaTdUhZDAKMmRXtgsg4u7DhOzaL0gpaJJHV1dfXMzFQE2QV1X/DZnFhYS8nVS1CB6MWZ49aTCuQNWpx0ajFNAJZoxYX0cviwvo7GHVvUf0bmjfuc4obPbOK6l8gW5pT1BjKQC+QCa3w6d6NoRQAAMvu91kVpuyFAAAAAElFTkSuQmCC"/>
    </defs>
    </svg>
`;
  }

  private getMoveUpIcon(): string {
    return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 12V4M4 8L8 4L12 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }

  private getMoveDownIcon(): string {
    return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 4V12M4 8L8 12L12 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }

  private getDeleteIcon(): string {
    return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 4H14M6 4V2H10V4M6 7V12M10 7V12M3 4L4 14H12L13 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }

  static get toolBoxIcon(): string {
    return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" viewBox="0 0 96 120" x="0px" y="0px"><path d="M16.004 86c-0.808 0-1.536-0.484-1.848-1.236s-0.14-1.6 0.432-2.18c2.212-2.22 6.208-7.66 8.88-13.52-13.296-5.82-21.468-16.056-21.468-27.064 0-17.648 20.636-32 46-32s46 14.352 46 32c0 17.64-20.636 32-46 32-1.644 0-3.256-0.084-4.852-0.204-7.528 8.096-16.656 12.204-27.144 12.204zM48.004 14c-23.16 0-42 12.572-42 28 0 9.844 7.984 19.088 20.832 24.132 0.496 0.196 0.896 0.584 1.104 1.080 0.208 0.488 0.22 1.048 0.020 1.536-1.864 4.652-4.756 9.424-7.396 12.948 7.716-1.060 14.52-4.844 20.276-11.304 0.42-0.468 1.028-0.716 1.668-0.656 1.8 0.156 3.628 0.264 5.492 0.264 23.16 0 42-12.564 42-28s-18.836-28-41.996-28z" fill="#000000"/><path d="M46.004 54c-1.104 0-2-0.896-2-2v-6.252c0-1.1 0.896-2 2-2 6.424 0 10.752-3.992 10.752-9.936 0-5.752-3.896-9.928-9.252-9.928-5.592 0-10.5 4.736-10.5 10.116 0 1.104-0.896 2-2 2s-2-0.896-2-2c0-7.656 6.64-14.116 14.5-14.116 7.56 0 13.252 5.992 13.252 13.928 0 7.572-5.148 13.024-12.752 13.836v4.352c0 1.104-0.896 2-2 2z" fill="#000000"/><path d="M46.004 66c-1.104 0-2-0.896-2-2v-4c0-1.096 0.896-2 2-2s2 0.904 2 2v4c0 1.104-0.896 2-2 2z" fill="#000000"/></svg>`;
  }
}
